import { useCallback, useEffect, useState } from "react";
import { Archive, CheckCircle2, Loader2, RotateCcw, XCircle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";

const pretty = (s) => String(s || "").slice(0, 16).replace("T", " ");

export const ArchivePanel = ({ electionRef, status, onChanged }) => {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState("");
  const [mayArchive, setMayArchive] = useState(false);

  const load = useCallback(async () => {
    const res = await api.get("/election-archive",
      { params: electionRef ? { election_ref: electionRef } : {} });
    setItems(res.data.items);
    setMayArchive(!!res.data.may_archive);
  }, [electionRef]);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const run = async (key, fn, message) => {
    setBusy(key);
    try {
      const res = await fn();
      toast.success(message);
      await load();
      setDialog(null);
      onChanged?.();
      return res;
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
    return null;
  };

  const openArchive = async (ref) => {
    try {
      const res = await api.get(`/election-archive/${ref}`);
      setOpen(res.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const archived = (items || []).some((a) => a.election_ref === electionRef);

  return (
    <div className="space-y-4">
      <Panel title="The archive" testId="panel-archive"
             kicker="A finished election is sealed whole — rulebook, roll, ballot, every paper, the count and the certificate trail"
             actions={mayArchive && !archived && ["declared", "certified"].includes(status) && (
               <Button size="sm" className="h-8 rounded-full" data-testid="archive-button"
                       onClick={() => setDialog({ kind: "archive", minute: "" })}>
                 <Archive className="mr-1.5 h-3.5 w-3.5" /> Archive this election
               </Button>
             )}>
        {!items ? (
          <p className="text-[12.5px] text-muted-foreground">Opening the archive…</p>
        ) : items.length === 0 ? (
          <p className="text-[12.5px] text-muted-foreground" data-testid="archive-empty">
            Nothing has been archived yet. Once a result is declared the election can be sealed
            whole and read again years later.
          </p>
        ) : (
          <div className="space-y-2" data-testid="archive-list">
            {items.map((a) => (
              <button key={a.archive_ref} type="button" data-testid={`archive-${a.archive_ref}`}
                      onClick={() => openArchive(a.archive_ref)}
                      className={`flex w-full flex-wrap items-center justify-between gap-2 rounded-xl border px-3.5 py-3 text-left transition-colors duration-150 ${
                        open?.archive_ref === a.archive_ref
                          ? "border-primary/50 bg-primary/5" : "border-border hover:border-primary/30"}`}>
                <span>
                  <span className="block text-[12.5px] font-medium text-foreground">
                    {a.election_name}
                  </span>
                  <span className="block text-[10.5px] text-muted-foreground">
                    {a.archive_ref} · term {a.term_start} to {a.term_end} · {a.counts.papers} paper(s)
                    {" "}· sealed {pretty(a.archived_at)} by {a.archived_by}
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  {(a.replays || []).length > 0 && (
                    <Chip className={a.replays[a.replays.length - 1].matches
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                      : "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"}>
                      {a.replays.length} replay(s)
                    </Chip>
                  )}
                  <Chip className="border-border bg-muted text-muted-foreground">read only</Chip>
                </span>
              </button>
            ))}
          </div>
        )}
      </Panel>

      {open && (
        <Panel title={open.election_name} testId="panel-archive-open"
               kicker={`${open.archive_ref} · rules v${open.rules_version} · ${open.counts.roll} on the frozen roll`}
               actions={open.may_replay && (
                 <Button size="sm" variant="outline" className="h-8 rounded-full"
                         data-testid="replay-button"
                         onClick={() => setDialog({ kind: "replay", minute: "",
                           ref: open.archive_ref })}>
                   <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Replay the count
                 </Button>
               )}>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="kicker">What was sealed</p>
              <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground"
                 data-testid="archive-contents">
                {open.counts.papers} sealed paper(s), {open.counts.roll} on the frozen roll,{" "}
                {open.counts.nominations} nomination(s), the ballot{" "}
                {open.ballot?.ballot_ref}, the count {open.results?.results_ref} and{" "}
                {(open.signatures || []).length} commissioner signature(s).
                {open.archive_minute ? ` ${open.archive_minute}` : ""}
              </p>
              <p className="mt-2 text-[11px] text-muted-foreground">
                Commission: {(open.commission?.members || []).map((m) => m.name).join(", ")}
              </p>
            </div>
            <div>
              <p className="kicker">The result as certified</p>
              <div className="mt-1.5 space-y-1">
                {(open.results?.sections || []).map((s) => (
                  <p key={s.section_ref} className="text-[12px] text-muted-foreground"
                     data-testid={`archive-section-${s.section_ref}`}>
                    <span className="text-foreground">{s.title}</span>:{" "}
                    {s.winners.map((w) => w.label).join(", ") || "no result"}
                  </p>
                ))}
              </div>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="kicker">The rulebook it was run under</p>
              <div className="mt-1.5 space-y-0.5" data-testid="archive-rules">
                {Object.entries(open.rules || {}).map(([group, rules]) => (
                  <p key={group} className="text-[11.5px] text-muted-foreground">
                    <span className="text-foreground">{group}</span>:{" "}
                    {Object.entries(rules || {}).map(([k, v]) =>
                      `${String(k).replace(/_/g, " ")} ${v}`).join(" · ")}
                  </p>
                ))}
              </div>
              <p className="kicker mt-3">The ballot as frozen</p>
              <div className="mt-1.5 space-y-0.5" data-testid="archive-ballot">
                {(open.sections || []).map((s) => (
                  <p key={s.section_ref} className="text-[11.5px] text-muted-foreground"
                     data-testid={`archive-ballot-${s.section_ref}`}>
                    <span className="text-foreground">{s.title}</span> · {s.seats} seat(s) ·{" "}
                    {String(s.method).replace(/_/g, " ")} · {s.options.join(", ")}
                  </p>
                ))}
              </div>
            </div>
            <div>
              <p className="kicker">The frozen roll ({(open.roll || []).length})</p>
              <p className="mt-1.5 text-[11.5px] leading-relaxed text-muted-foreground"
                 data-testid="archive-roll">
                {(open.roll || []).map((p) => p.name).join(", ") || "no roll was kept"}
              </p>
              <p className="kicker mt-3">
                The marked register ({(open.register || []).length}) — who voted, never what they
                marked
              </p>
              <div className="mt-1.5 max-h-40 space-y-0.5 overflow-y-auto"
                   data-testid="archive-register">
                {(open.register || []).map((r) => (
                  <p key={r.receipt} className="text-[11px] text-muted-foreground">
                    {r.name} · {pretty(r.at)} · receipt {r.receipt}
                  </p>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <p className="kicker">The certificate trail</p>
            <div className="mt-1.5 space-y-0.5" data-testid="archive-trail">
              {(open.timeline || []).map((t, i) => (
                <p key={`${t.status}-${i}`} className="text-[11.5px] text-muted-foreground">
                  {String(t.status).replace(/_/g, " ")} · {pretty(t.at)} · {t.by}
                  {t.note ? ` — ${t.note}` : ""}
                </p>
              ))}
              {(open.signatures || []).length > 0 && (
                <p className="text-[11.5px] text-muted-foreground">
                  Signed by {(open.signatures || []).map((s) => s.name).join(", ")}.
                </p>
              )}
            </div>
          </div>
          {(open.replays || []).length > 0 && (
            <div className="mt-4">
              <p className="kicker">Replays</p>              <div className="mt-1.5 space-y-1" data-testid="replay-history">
                {open.replays.map((r) => (
                  <p key={r.replay_ref} className="text-[11.5px] text-muted-foreground"
                     data-testid={`replay-${r.replay_ref}`}>
                    {r.matches
                      ? <CheckCircle2 className="mr-1 inline h-3 w-3 text-emerald-500" />
                      : <XCircle className="mr-1 inline h-3 w-3 text-red-500" />}
                    {r.replay_ref} · {pretty(r.at)} by {r.by} · {r.papers} paper(s) ·{" "}
                    {r.matches ? "identical to the certified count"
                      : `${r.differences.length} difference(s)`}
                    {r.minute ? ` — ${r.minute}` : ""}
                  </p>
                ))}
              </div>
            </div>
          )}
        </Panel>
      )}

      <Dialog open={dialog?.kind === "archive"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="archive-dialog">
          <DialogHeader><DialogTitle>Archive this election</DialogTitle>
            <DialogDescription>
              Everything is sealed as one record and becomes read-only: the rulebook version, the
              frozen roll, the ballot, every paper, the count and the certificate trail. The
              Commission's powers end here.
            </DialogDescription></DialogHeader>
          <Textarea rows={3} data-testid="archive-minute" placeholder="Note for the record"
                    value={dialog?.minute || ""}
                    onChange={(e) => setDialog({ ...dialog, minute: e.target.value })} />
          <DialogFooter>
            <Button data-testid="archive-save" disabled={busy === "archive"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("archive",
                      () => api.post(`/elections/${electionRef}/archive`,
                        { minute: dialog.minute }),
                      "The election is sealed whole and read-only")}>
              {busy === "archive" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Seal it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "replay"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="replay-dialog">
          <DialogHeader><DialogTitle>Replay the count</DialogTitle>
            <DialogDescription>
              The archived papers are counted again under the same rules and checked against the
              figures that were certified. Nothing is changed either way.
            </DialogDescription></DialogHeader>
          <Textarea rows={3} data-testid="replay-minute" placeholder="Why are you replaying it?"
                    value={dialog?.minute || ""}
                    onChange={(e) => setDialog({ ...dialog, minute: e.target.value })} />
          <DialogFooter>
            <Button data-testid="replay-save" disabled={busy === "replay"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={async () => {
                      const res = await run("replay",
                        () => api.post(`/election-archive/${dialog.ref}/replay`,
                          { minute: dialog.minute }),
                        "The replay is recorded");
                      if (res) await openArchive(dialog.ref);
                    }}>
              {busy === "replay" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Replay it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
